/**
 * Add Asset Panel — slide-over form for manually adding a new asset entry.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ScrollArea } from "@/ui/primitives/scroll-area";

const ASSET_TYPES = [
  "Cash at Bank",
  "Accounts Receivable",
  "Assets Subject to Lease",
  "Interests in Land",
  "Plant and Equipment",
  "Stock / Inventory",
  "Intangible Assets",
  "Contingent Assets",
] as const;

type AssetTypeOption = (typeof ASSET_TYPES)[number];

function isAssetTypeOption(value: string): value is AssetTypeOption {
  return (ASSET_TYPES as readonly string[]).includes(value);
}

interface AddAssetPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function AddAssetPanel({
  jobId,
  taskId,
  open,
  onClose,
  onCreated,
}: AddAssetPanelProps) {
  const [description, setDescription] = useState("");
  const [assetType, setAssetType] = useState<AssetTypeOption>("Cash at Bank");
  const [location, setLocation] = useState("");
  const [secured, setSecured] = useState(false);
  const [status, setStatus] = useState<"Identified" | "Missing">("Identified");
  const [notes, setNotes] = useState("");
  const [initialValue, setInitialValue] = useState("");

  const addAsset = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations.addManualAsset,
    ),
  });

  async function handleSubmit() {
    if (!description.trim()) return;

    const result = await addAsset.mutateAsync({
      jobId,
      taskId,
      description: description.trim(),
      assetType,
      subjectToSecurityInterest: secured,
      location: location.trim() || "N/A",
      assetStatus: status,
      notes: notes.trim(),
      assetPhase: "Pre-Appointment",
      initialValue: initialValue ? parseFloat(initialValue) : undefined,
    });

    onCreated(result as unknown as string);
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md">
        <div className="px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold">Add New Asset</SheetTitle>
            <SheetDescription>
              Manually add an asset to the summary.
            </SheetDescription>
          </SheetHeader>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-6 pb-6">
            {/* Description */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Description *
              </label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One sentence description of the asset"
                className="h-9 text-sm"
              />
            </div>

            {/* Type */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Type
              </label>
              <Select
                value={assetType}
                onValueChange={(value) => {
                  if (isAssetTypeOption(value)) setAssetType(value);
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Location
              </label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Address or N/A"
                className="h-9 text-sm"
              />
            </div>

            {/* Security Interest */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Subject to Security Interest
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={secured ? "outline" : "secondary"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setSecured(false)}
                >
                  No
                </Button>
                <Button
                  type="button"
                  variant={secured ? "secondary" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setSecured(true)}
                >
                  Yes
                </Button>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={status === "Identified" ? "secondary" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setStatus("Identified")}
                >
                  Identified
                </Button>
                <Button
                  type="button"
                  variant={status === "Missing" ? "secondary" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setStatus("Missing")}
                >
                  Missing
                </Button>
              </div>
            </div>

            {/* Initial Value */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Initial Value (optional)
              </label>
              <Input
                type="number"
                value={initialValue}
                onChange={(e) => setInitialValue(e.target.value)}
                placeholder="0.00"
                className="h-9 text-sm tabular-nums"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any relevant details..."
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Submit */}
            <Button
              className="w-full"
              disabled={!description.trim() || addAsset.isPending}
              onClick={handleSubmit}
            >
              {addAsset.isPending ? "Adding..." : "Add Asset"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
